import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import './assets/styles/main.css'

const app = createApp(App)
app.use(createPinia())
app.use(router)

// ── Résilience longue durée (centre d'appels ouvert toute la journée) ──────
// Objectif : aucun gel silencieux nécessitant un refresh manuel.

// 1) Erreurs de chargement de chunk (routes en lazy import). Surviennent quand
//    le serveur a été REDÉPLOYÉ pendant que l'app était ouverte : les anciens
//    chunks (hash) ont disparu (rsync --delete), donc ouvrir une vue pas encore
//    chargée échoue → la page semble figée. On recharge automatiquement pour
//    récupérer les nouveaux chunks — transparent pour l'agent.
const CHUNK_RE =
  /(dynamically imported module|module script failed|importing a module script|ChunkLoadError|Loading chunk\b)/i
function isChunkError(e: unknown): boolean {
  const msg = (e as { message?: string })?.message ?? String(e ?? '')
  return CHUNK_RE.test(msg)
}
function reloadOnce(): void {
  // Anti-boucle : si on a déjà rechargé il y a moins de 10 s (le reload n'a pas
  // réglé le souci), on n'enchaîne pas. Au-delà (prochain déploiement), un
  // nouveau chunk error rechargera de nouveau.
  const KEY = 'pexitalk-chunk-reload-at'
  const last = Number(sessionStorage.getItem(KEY) || 0)
  if (Date.now() - last < 10_000) return
  sessionStorage.setItem(KEY, String(Date.now()))
  window.location.reload()
}

// Hook officiel Vite : un preload de module dynamique a échoué.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault() // empêche l'exception de remonter et de bloquer
  reloadOnce()
})

// Échec de navigation (import de route lazy raté) → reload si c'est un chunk.
router.onError((err) => {
  if (isChunkError(err)) reloadOnce()
  else console.error('[router error]', err)
})

// 2) Filet global : jusqu'ici AUCUN handler → une erreur non gérée cassait
//    l'app en silence. On la journalise (diagnostic) sans tout figer ; on ne
//    recharge que si c'est un chunk.
app.config.errorHandler = (err, _instance, info) => {
  if (isChunkError(err)) { reloadOnce(); return }
  console.error('[vue error]', info, err)
}
window.addEventListener('unhandledrejection', (ev) => {
  if (isChunkError(ev.reason)) { ev.preventDefault(); reloadOnce(); return }
  console.error('[unhandledrejection]', ev.reason)
})
window.addEventListener('error', (ev) => {
  if (isChunkError(ev.error ?? ev.message)) reloadOnce()
})

app.mount('#app')
